import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Wallet, Upload, ArrowRight, Pencil, Globe, Calendar as CalendarIcon, ScaleIcon, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFinancePermissions } from "@/components/finance/banking/update_account_balance/useFinancePermissions";
import UpdateAccountBalanceDialog from "@/components/finance/banking/update_account_balance/UpdateAccountBalanceDialog";
import { useBankAccountsWithBalances } from "@/hooks/finance/useBankBalances";
import {
  useComputedBankBalances,
  pickLatestSnapshot,
  pickLatestTx,
} from "@/hooks/finance/useComputedBankBalances";
import { useClosingAsOf } from "@/hooks/finance/reconciliation/useClosingAsOf";
import { useConsolidatedBalances, type AccountBalanceInput } from "@/hooks/finance/useConsolidatedBalances";
import { CurrencySelect } from "@/components/ui/currency-select";
const validCurrencyCodes = new Set([
  "USD",
  "EUR",
  "GBP",
  "ETB",
  "AED",
  "SAR",
  "KES",
  "TZS",
  "UGX",
  "RWF",
  "ZAR",
  "EGP",
  "NGN",
  "GHS",
  "INR",
  "CNY",
  "JPY",
  "KRW",
  "CAD",
  "AUD",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "TRY",
  "BRL",
  "MXN",
]);

const formatCurrency = (amount: number | null, currency: string | null) => {
  if (amount === null || Number.isNaN(amount)) return "-";

  const currencyCode = currency || "ETB";

  if (validCurrencyCodes.has(currencyCode)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(amount);
  }

  return `${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })} ${currencyCode}`;
};

const formatDateShort = (iso: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatDateTimeShort = (iso: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// Legacy helper kept (unused) — resolver now lives in useRealClosing as pickEffectiveClosingAsOf

const formatUserName = (
  p?: { first_name?: string | null; last_name?: string | null } | null,
  fallbackId?: string | null
) => {
  const full = [p?.first_name, p?.last_name].filter(Boolean).join(" ");
  return full || (fallbackId ? `User ${String(fallbackId).slice(0, 8)}…` : "Unknown user");
};

const BankBalancesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { roleLabel, canUpdateBalance } = useFinancePermissions();

  const [updateOpen, setUpdateOpen] = React.useState(false);
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [consolidatedCurrency, setConsolidatedCurrency] = React.useState("USD");

  const { data: accounts = [], isLoading } = useBankAccountsWithBalances();

  const bankAccountIds = React.useMemo(() => accounts.map((a) => a.id), [accounts]);

  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

  // "As of" date — drives both the per-account effective closing AND the
  // consolidated total, so this page can be aligned with the Cash Flow
  // Integrity panel by picking the same dateTo.
  const [asOfDate, setAsOfDate] = React.useState<string>(today);

  const closingQ = useClosingAsOf({
    bankAccountIds,
    asOfDate,
    refreshKey,
  });

  const {
    computedAccounts,
    balancesByCurrency,
    snapshotsByAccount,
    txByAccount,
    profilesById,
    showLoading,
    transactionsCountByAccount,
  } = useComputedBankBalances({
    accounts,
    isAccountsLoading: isLoading,
    refreshKey,
  });

  // Build account balance inputs for consolidated view using effective closing as of selected date
  const consolidatedInputs: AccountBalanceInput[] = React.useMemo(() => {
    return computedAccounts.map((account) => {
      const effective = closingQ.data?.get(account.id);
      const currency =
        effective?.currency ?? account.currency ?? account._computed_currency ?? "ETB";
      return {
        id: account.id,
        name: account.name,
        currency,
        balance: effective?.closing_balance ?? null,
      };
    });
  }, [computedAccounts, closingQ.data]);

  const consolidated = useConsolidatedBalances(consolidatedInputs, consolidatedCurrency);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold">Account Closing Balances</h1>
            <p className="text-muted-foreground">
              Showing closing balances as of{" "}
              <span className="text-foreground font-medium">
                {format(parseISO(asOfDate), "PPP")}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Your account role: {roleLabel?.charAt(0).toUpperCase() + roleLabel?.slice(1)}
            </p>
          </div>

          <div className="flex gap-2 items-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("justify-start text-left font-normal w-[200px]")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  As of {format(parseISO(asOfDate), "MMM dd, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={parseISO(asOfDate)}
                  onSelect={(d) => d && setAsOfDate(format(d, "yyyy-MM-dd"))}
                  disabled={(d) => d > new Date()}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              onClick={() => navigate("/finance/banking/transactions")}
            >
              View Transactions
            </Button>

            <Button onClick={() => navigate("/finance/banking/transactions")}>
              <Upload className="h-4 w-4 mr-2" />
              Import Statement
            </Button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
          {Object.entries(balancesByCurrency).length === 0 ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Totals
                </CardTitle>
                <Wallet className="h-4 w-4 text-muted-foreground" />
              </CardHeader>

              <CardContent>
                <div className="text-lg font-bold">-</div>
                <p className="text-xs text-muted-foreground mt-1">No balances yet</p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(balancesByCurrency).map(([currency, total]) => (
              <Card key={currency}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total {currency}
                  </CardTitle>
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                </CardHeader>

                <CardContent>
                  <div className="text-lg font-bold">{formatCurrency(total, currency)}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Across{" "}
                    {
                      computedAccounts.filter(
                        (a) => (a._computed_currency || a.currency || "ETB") === currency
                      ).length
                    }{" "}
                    accounts
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">Account Details</h2>

          {showLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-1/3 mb-3 animate-pulse" />
                <div className="h-4 bg-muted rounded w-2/3 mb-2 animate-pulse" />
                <div className="h-4 bg-muted rounded w-1/2 animate-pulse" />
              </CardContent>
            </Card>
          ) : computedAccounts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Bank Accounts</h3>
                <p className="text-muted-foreground mb-4">
                  Add bank accounts to start tracking balances
                </p>
                <Button onClick={() => navigate("/finance/settings/bank-accounts")}>
                  Add Bank Account
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Breakdown by account
                </CardTitle>
              </CardHeader>

              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b">
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 w-[60px]">
                          #
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Account
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Currency
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Manual closing balance
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Closing balance from latest transaction
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Closing balance used in reports
                        </th>
                        <th className="text-right font-medium text-muted-foreground px-4 py-3 whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {computedAccounts.map((account, index) => {
                        const snap = pickLatestSnapshot(snapshotsByAccount.get(account.id) ?? []);
                        const tx = pickLatestTx(txByAccount.get(account.id) ?? []);

                        const effectiveClosing = closingQ.data?.get(account.id) ?? null;

                        const snapProf = snap?.user_id ? profilesById.get(snap.user_id) : undefined;
                        const snapBy = snap
                          ? formatUserName(
                            snapProf
                              ? {
                                first_name: snapProf.first_name,
                                last_name: snapProf.last_name,
                              }
                              : null,
                            snap.user_id
                          )
                          : null;

                        const txCount = transactionsCountByAccount.get(account.id) ?? 0;

                        const hasSnap = !!snap && snap.closing_balance != null;
                        const hasTx = !!tx && tx.end_balance != null;
                        const hasAnySourceData = hasSnap || hasTx;

                        const navigateToTx = () =>
                          navigate(`/finance/banking/transactions?account=${account.id}`);

                        return (
                          <tr key={account.id} className="border-b hover:bg-muted/30">
                            <td className="px-4 py-2 text-muted-foreground align-middle">
                              {index + 1}
                            </td>

                            <td className="px-4 py-2 align-middle w-[220px] max-w-[220px]">
                              <div className="font-medium whitespace-normal break-words">
                                {account.name}
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                {account.bank_name || account.account_type || "Bank Account"}
                                {account.account_number ? (
                                  <> • •••• {String(account.account_number).slice(-4)}</>
                                ) : null}
                              </div>
                            </td>

                            <td className="px-4 py-2 align-middle w-[90px]">
                              <span className="font-medium">
                                {account.currency || account._computed_currency || "—"}
                              </span>
                            </td>

                            <td className="px-4 py-2 align-middle w-[240px] max-w-[240px]">
                              {snap ? (
                                <>
                                  <div className="font-medium whitespace-normal break-words">
                                    {formatCurrency(
                                      snap.closing_balance ?? null,
                                      snap.currency ?? account.currency ?? "ETB"
                                    )}
                                  </div>

                                  <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                    Balance date:{" "}
                                    <span className="text-foreground">
                                      {formatDateShort(snap.balance_date)}
                                    </span>
                                  </div>

                                  <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                    Entered by <span className="text-foreground">{snapBy}</span>
                                    {snap.created_at ? (
                                      <> • Entered at {formatDateTimeShort(snap.created_at)}</>
                                    ) : null}
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs text-muted-foreground">—</div>
                              )}
                            </td>

                            <td className="px-4 py-2 align-middle w-[240px] max-w-[240px]">
                              {tx ? (
                                <>
                                  <div className="font-medium whitespace-normal break-words">
                                    {formatCurrency(
                                      tx.end_balance ?? null,
                                      tx.end_balance_currency ?? account.currency ?? "ETB"
                                    )}
                                  </div>

                                  <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                    Latest transaction date:{" "}
                                    <span className="text-foreground">
                                      {formatDateShort(tx.transaction_date)}
                                    </span>
                                  </div>

                                  <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                    {txCount === 0
                                      ? "No transactions loaded for this account"
                                      : `${txCount} transactions loaded for this account`}
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs text-muted-foreground">—</div>
                              )}
                            </td>

                            <td className="px-4 py-2 align-middle w-[240px] max-w-[240px]">
                              {!effectiveClosing || effectiveClosing.closing_balance == null ? (
                                <div className="text-xs text-muted-foreground">—</div>
                              ) : (() => {
                                const dayKey = effectiveClosing.balance_day ?? "";
                                const isManual = effectiveClosing.source === "manual";
                                const isTx = effectiveClosing.source === "transaction";
                                // Stale: a newer transaction exists after the resolved day,
                                // but on/before asOfDate
                                const txDay = tx ? String(tx.transaction_date).slice(0, 10) : null;
                                const isStale =
                                  isManual &&
                                  txDay != null &&
                                  txDay > dayKey &&
                                  txDay <= asOfDate;

                                return (
                                  <>
                                    <div className="font-semibold whitespace-normal break-words">
                                      {formatCurrency(
                                        effectiveClosing.closing_balance,
                                        effectiveClosing.currency ?? account.currency ?? "ETB"
                                      )}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-1 mt-1">
                                      {isManual && (
                                        <Badge variant="default" className="text-[10px] py-0 px-1.5">
                                          Manual snapshot
                                        </Badge>
                                      )}
                                      {isTx && (
                                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                                          Transaction end-balance
                                        </Badge>
                                      )}
                                      {isStale && (
                                        <Badge variant="destructive" className="text-[10px] py-0 px-1.5">
                                          Stale
                                        </Badge>
                                      )}
                                    </div>

                                    {dayKey ? (
                                      <div className="text-xs text-muted-foreground whitespace-normal break-words mt-1">
                                        As of {formatDateShort(dayKey)}
                                      </div>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </td>


                            <td className="px-4 py-2 align-middle text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-2">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  title="Reconcile in Cash Flow Integrity"
                                  onClick={() =>
                                    navigate(
                                      `/finance/reports/cash-flow-new?account=${account.id}&dateTo=${asOfDate}`
                                    )
                                  }
                                >
                                  <ScaleIcon className="h-4 w-4" />
                                </Button>

                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  title="View transactions"
                                  onClick={navigateToTx}
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </Button>

                                {canUpdateBalance ? (
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-8 w-8"
                                    title="Update current balance"
                                    onClick={() => {
                                      setSelectedAccountId(account.id);
                                      setSelectedAccountName(account.name ?? null);
                                      setUpdateOpen(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Consolidated Balance View */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Consolidated Balance</h2>
            <CurrencySelect
              value={consolidatedCurrency}
              onValueChange={setConsolidatedCurrency}
              className="w-28"
            />
          </div>

          {closingQ.isError ? (
            <Card className="border-destructive">
              <CardContent className="p-6 text-sm text-destructive">
                Closing balances could not be loaded, so no consolidated total is shown.
                Please retry in a moment.
              </CardContent>
            </Card>
          ) : consolidated.isLoading || closingQ.isLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-1/3 animate-pulse" />
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 mb-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Grand Total ({consolidatedCurrency})
                    </CardTitle>
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(consolidated.grandTotal, consolidatedCurrency)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Closing balances as of {format(parseISO(asOfDate), "PPP")}
                    </p>
                    {consolidated.unconvertedCount > 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="mt-2 inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                              onClick={() => navigate("/finance/exchange-rates")}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {consolidated.unconvertedCount} account(s) excluded — missing FX rate
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="text-xs font-medium mb-1">Excluded accounts:</div>
                            <ul className="text-xs space-y-0.5">
                              {consolidated.converted
                                .filter(
                                  (c) => c.originalBalance != null && c.convertedBalance == null
                                )
                                .map((c) => (
                                  <li key={c.accountId}>
                                    {c.accountName} ({c.originalCurrency})
                                  </li>
                                ))}
                            </ul>
                            <div className="text-[10px] text-muted-foreground mt-2">
                              Click to manage exchange rates
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Breakdown in {consolidatedCurrency}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b">
                          <th className="text-left font-medium text-muted-foreground px-4 py-3">#</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-3">Account</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-3">Original Balance</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-3">Rate</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-3">Method</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-3">In {consolidatedCurrency}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consolidated.converted.map((row, i) => (
                          <tr key={row.accountId} className="border-b hover:bg-muted/30">
                            <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-4 py-2 font-medium">{row.accountName}</td>
                            <td className="px-4 py-2 text-right">
                              {row.originalBalance != null
                                ? formatCurrency(row.originalBalance, row.originalCurrency)
                                : "—"}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-xs">
                              {row.rateUsed != null ? row.rateUsed.toFixed(4) : "—"}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground">
                              {row.rateMethod === 'same' && 'Same currency'}
                              {row.rateMethod === 'direct' && row.ratePair}
                              {row.rateMethod === 'inverse' && row.ratePair}
                              {row.rateMethod === 'etb-routed' && row.ratePair}
                              {row.rateMethod === null && row.originalBalance != null && (
                                <span className="text-destructive">No rate</span>
                              )}
                              {row.rateMethod === null && row.originalBalance == null && "—"}
                            </td>
                            <td className="px-4 py-2 text-right font-semibold">
                              {row.convertedBalance != null
                                ? formatCurrency(row.convertedBalance, consolidatedCurrency)
                                : "—"}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 font-bold">
                          <td colSpan={5} className="px-4 py-3 text-right">Grand Total</td>
                          <td className="px-4 py-3 text-right">
                            {formatCurrency(consolidated.grandTotal, consolidatedCurrency)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      <UpdateAccountBalanceDialog
        open={updateOpen}
        onOpenChange={setUpdateOpen}
        bankAccountId={selectedAccountId}
        bankAccountName={selectedAccountName}
        onSaved={() => {
          setRefreshKey((k) => k + 1);
          queryClient.invalidateQueries({ queryKey: ["real_closing_matrix_rpc"] });
          queryClient.invalidateQueries({ queryKey: ["bank_closing_as_of"] });
        }}
      />
    </Layout>
  );
};

export default BankBalancesPage;